import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  Alert,
  Box,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  RowActions,
  Drawer,
  IconButton,
  Icons,
  MenuItem,
  Stack,
  StatusChip,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  iconSize,
  useConfirm,
  useRowActionsColumn,
} from '@munifocus/ui';
import { formatCents, parseMoneyToCents } from '@munifocus/billing';
import { nbDay, type ContractorInvoiceView, type RateCardView } from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { PageIntro } from '../../components/PageIntro';
import { useOptionalScope } from '../../providers/ScopeProvider';
import { apiErrorMessage, useMutationToast } from '../../lib/mutation-toast';
import {
  downloadInvoice,
  useCompanyInvoice,
  useCompanyInvoices,
  useDraftInvoice,
  useMoveInvoice,
  useRateCards,
  useReceivedInvoice,
  useReceivedInvoices,
  useSaveRateCard,
} from '../../lib/operations-api';
import {
  centsForInput,
  formatMinutes,
  invoiceStatusKind,
  monthLabel,
  recentMonths,
} from '../../lib/operations-format';
import { formatDate } from '../../lib/relative-time';
import { LoadError, Loading, OpsScopeNotice, useOpsMunicipality } from './shared';

const intl = (lang: string) => (lang.startsWith('fr') ? 'fr-CA' : 'en-CA');

/**
 * The contractor's invoices to the towns it works for, and the towns' side of
 * the same invoices.
 *
 * A contractor's administrator keeps its rates per town and drafts each
 * month's invoice from approved time; a draft is rebuilt on demand, sending it
 * numbers and freezes it, and voiding releases what it billed. A town's
 * finance office and clerk see what they were sent — never a draft — and can
 * download it in Word or PDF.
 */
export function Invoices() {
  const { t } = useTranslation();
  const scope = useOptionalScope();
  const status = scope?.operations ?? null;
  const companies = status?.companies ?? [];
  const received = useOpsMunicipality((m) => m.canReadInvoices);
  const activeCompany =
    scope?.active?.kind === 'company'
      ? companies.find((c) => c.companyId === scope.active?.id)
      : undefined;
  const [picked, setPicked] = useState<string>('');
  const companyId = activeCompany?.companyId ?? (picked || companies[0]?.companyId);

  return (
    <AppShell title={t('gov.ops.invoices.title')}>
      <PageIntro>
        {t(companyId ? 'gov.ops.invoices.introCompany' : 'gov.ops.invoices.introTown')}
      </PageIntro>
      {!status && <Loading />}
      {status && companyId && (
        <>
          {!activeCompany && companies.length > 1 && (
            <TextField
              select
              size="small"
              label={t('gov.ops.invoices.company')}
              value={companyId}
              onChange={(e) => setPicked(e.target.value)}
              sx={{ mb: 2, minWidth: 260 }}
            >
              {companies.map((c) => (
                <MenuItem key={c.companyId} value={c.companyId}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <CompanyBooks companyId={companyId} />
        </>
      )}
      {status && !companyId && received.municipality && (
        <ReceivedInvoices lgId={received.municipality.localGovernmentId} />
      )}
      {status && !companyId && !received.municipality && (
        <OpsScopeNotice reason={received.reason ?? 'notEnabled'} />
      )}
    </AppShell>
  );
}

// ── The contractor's side ──────────────────────────────────────────────────

function CompanyBooks({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'invoices' | 'rates'>('invoices');
  return (
    <>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="invoices" label={t('gov.ops.invoices.tabs.invoices')} />
        <Tab value="rates" label={t('gov.ops.invoices.tabs.rates')} />
      </Tabs>
      {tab === 'invoices' ? (
        <CompanyInvoices companyId={companyId} />
      ) : (
        <Rates companyId={companyId} />
      )}
    </>
  );
}

function InvoiceStatusChip({ invoice }: { invoice: ContractorInvoiceView }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      status={invoiceStatusKind(invoice.displayStatus)}
      label={t(`gov.ops.invoices.statuses.${invoice.displayStatus}`)}
    />
  );
}

function invoiceColumns(t: (key: string) => string, lang: string, who: 'town' | 'company') {
  return [
    {
      key: 'month',
      label: t('gov.ops.invoices.month'),
      primary: true,
      sortValue: (i: ContractorInvoiceView) => i.month,
      cell: (i: ContractorInvoiceView) => (
        <Stack>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {monthLabel(i.month, lang)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {who === 'company' ? i.localGovernmentName : i.companyName}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'number',
      label: t('gov.ops.invoices.number'),
      sortValue: (i: ContractorInvoiceView) => i.number,
      cell: (i: ContractorInvoiceView) => i.number || '—',
    },
    {
      key: 'total',
      label: t('gov.ops.invoices.total'),
      align: 'right' as const,
      sortValue: (i: ContractorInvoiceView) => i.totalCents,
      cell: (i: ContractorInvoiceView) => formatCents(i.totalCents, i.currency, intl(lang)),
    },
    {
      key: 'due',
      label: t('gov.ops.invoices.due'),
      sortValue: (i: ContractorInvoiceView) => i.dueAt ?? '',
      cell: (i: ContractorInvoiceView) => formatDate(i.dueAt, lang),
    },
    {
      key: 'status',
      label: t('gov.ops.invoices.status'),
      sortValue: (i: ContractorInvoiceView) => i.displayStatus,
      cell: (i: ContractorInvoiceView) => <InvoiceStatusChip invoice={i} />,
    },
  ];
}

function CompanyInvoices({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const invoices = useCompanyInvoices(companyId);
  const rates = useRateCards(companyId);
  const [drafting, setDrafting] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const towns = rates.data ?? [];

  if (invoices.isError) {
    return (
      <LoadError onRetry={() => void invoices.refetch()}>
        {t('gov.ops.invoices.loadError')}
      </LoadError>
    );
  }
  return (
    <>
      <DataTable
        label={t('gov.ops.invoices.listTitle')}
        rows={invoices.data ?? []}
        getRowKey={(i) => i.id}
        isLoading={invoices.isLoading}
        emptyLabel={t('gov.ops.invoices.empty')}
        pageSize={25}
        initialSortKey="month"
        initialSortDir="desc"
        onRowClick={(i) => setOpen(i.id)}
        action={
          towns.length > 0 ? (
            <ActionButton
              label={t('gov.ops.invoices.draft')}
              icon={Icons.Plus}
              onClick={() => setDrafting(true)}
            />
          ) : null
        }
        columns={[
          ...invoiceColumns(t, i18n.language, 'company'),
          { ...actionsColumn, cell: (i) => <RowActions onView={() => setOpen(i.id)} /> },
        ]}
      />
      {drafting && (
        <DraftDialog
          companyId={companyId}
          towns={towns}
          onClose={() => setDrafting(false)}
          onDrafted={(id) => {
            setDrafting(false);
            setOpen(id);
          }}
        />
      )}
      <CompanyInvoiceDrawer companyId={companyId} id={open} onClose={() => setOpen(null)} />
    </>
  );
}

function DraftDialog({
  companyId,
  towns,
  onClose,
  onDrafted,
}: {
  companyId: string;
  towns: RateCardView[];
  onClose: () => void;
  onDrafted: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const draft = useDraftInvoice(companyId);
  const months = recentMonths();
  const [lgId, setLgId] = useState(towns[0]?.localGovernmentId ?? '');
  // Last month by default: invoices are drafted once a month has closed.
  const [month, setMonth] = useState(months[1] ?? months[0] ?? '');
  const [language, setLanguage] = useState<'en' | 'fr'>(
    i18n.language.startsWith('fr') ? 'fr' : 'en',
  );
  const town = towns.find((r) => r.localGovernmentId === lgId);

  const submit = async () => {
    try {
      const invoice = await draft.mutateAsync({ localGovernmentId: lgId, month, language });
      toast.success('gov.ops.invoices.drafted');
      onDrafted(invoice.id);
    } catch (err) {
      toast.error(err, 'gov.ops.invoices.draftFailed');
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.ops.invoices.draftTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('gov.ops.invoices.municipality')}
            value={lgId}
            onChange={(e) => setLgId(e.target.value)}
          >
            {towns.map((r) => (
              <MenuItem key={r.localGovernmentId} value={r.localGovernmentId}>
                {r.localGovernmentName}
              </MenuItem>
            ))}
          </TextField>
          {town && !town.configured && (
            <Alert severity="warning">{t('gov.ops.invoices.noRates')}</Alert>
          )}
          <TextField
            select
            label={t('gov.ops.invoices.month')}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <MenuItem key={m} value={m}>
                {monthLabel(m, i18n.language)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('gov.ops.invoices.language')}
            value={language}
            onChange={(e) => setLanguage(e.target.value as 'en' | 'fr')}
            helperText={t('gov.ops.invoices.languageHint')}
          >
            <MenuItem value="en">{t('gov.ops.invoices.languages.en')}</MenuItem>
            <MenuItem value="fr">{t('gov.ops.invoices.languages.fr')}</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary">
            {t('gov.ops.invoices.draftHint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!lgId || !month || !town?.configured || draft.isPending}
          onClick={() => void submit()}
        >
          {t('gov.ops.invoices.draft')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CompanyInvoiceDrawer({
  companyId,
  id,
  onClose,
}: {
  companyId: string;
  id: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useMutationToast();
  const confirm = useConfirm();
  const invoice = useCompanyInvoice(companyId, id);
  const draft = useDraftInvoice(companyId);
  const move = useMoveInvoice(companyId);
  const [paying, setPaying] = useState(false);
  const data = invoice.data;

  const rebuild = async () => {
    if (!data) return;
    try {
      await draft.mutateAsync({
        localGovernmentId: data.localGovernmentId,
        month: data.month,
        language: data.language,
      });
      await invoice.refetch();
      toast.success('gov.ops.invoices.rebuilt');
    } catch (err) {
      toast.error(err, 'gov.ops.invoices.draftFailed');
    }
  };

  const act = async (action: 'send' | 'void') => {
    if (!data) return;
    const ok = await confirm({
      title: t(`gov.ops.invoices.confirm.${action}.title`),
      body: t(`gov.ops.invoices.confirm.${action}.body`, {
        town: data.localGovernmentName,
      }),
      confirmLabel: t(`gov.ops.invoices.actions.${action}`),
      cancelLabel: t('common.actions.cancel'),
    });
    if (!ok) return;
    try {
      await move.mutateAsync({ id: data.id, action });
      await invoice.refetch();
      toast.success(`gov.ops.invoices.moved.${action}`);
    } catch (err) {
      toast.error(err, 'gov.ops.invoices.actionFailed');
    }
  };

  return (
    <InvoiceDrawer
      open={!!id}
      onClose={onClose}
      invoice={data}
      isLoading={invoice.isLoading}
      isError={invoice.isError}
      onRetry={() => void invoice.refetch()}
      download={(format) => data && downloadInvoice({ companyId }, data, format)}
      actions={
        data && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {data.status === 'draft' && (
              <>
                <Button
                  variant="outlined"
                  disabled={draft.isPending}
                  onClick={() => void rebuild()}
                >
                  {t('gov.ops.invoices.actions.rebuild')}
                </Button>
                <Button
                  variant="contained"
                  disabled={move.isPending || !data.lines?.length}
                  onClick={() => void act('send')}
                >
                  {t('gov.ops.invoices.actions.send')}
                </Button>
              </>
            )}
            {data.status === 'sent' && (
              <Button variant="contained" onClick={() => setPaying(true)}>
                {t('gov.ops.invoices.actions.paid')}
              </Button>
            )}
            {(data.status === 'draft' || data.status === 'sent') && (
              <Button color="error" disabled={move.isPending} onClick={() => void act('void')}>
                {t('gov.ops.invoices.actions.void')}
              </Button>
            )}
          </Stack>
        )
      }
    >
      {data && paying && (
        <PaidDialog
          onClose={() => setPaying(false)}
          onSave={async (paidOn, paymentReference) => {
            try {
              await move.mutateAsync({ id: data.id, action: 'paid', paidOn, paymentReference });
              await invoice.refetch();
              toast.success('gov.ops.invoices.moved.paid');
              setPaying(false);
            } catch (err) {
              toast.error(err, 'gov.ops.invoices.actionFailed');
            }
          }}
          pending={move.isPending}
        />
      )}
    </InvoiceDrawer>
  );
}

function PaidDialog({
  onClose,
  onSave,
  pending,
}: {
  onClose: () => void;
  onSave: (paidOn: string, reference: string) => Promise<void>;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const today = nbDay(new Date());
  const [paidOn, setPaidOn] = useState(today);
  const [reference, setReference] = useState('');
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(paidOn) && paidOn <= today;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('gov.ops.invoices.paidTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            type="date"
            label={t('gov.ops.invoices.paidOn')}
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
            error={!valid}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
          />
          <TextField
            label={t('gov.ops.invoices.paymentReference')}
            helperText={t('gov.ops.invoices.paymentReferenceHint')}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          onClick={() => void onSave(paidOn, reference.trim())}
        >
          {t('gov.ops.invoices.actions.paid')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Rates ───────────────────────────────────────────────────────────────────

function Rates({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const rates = useRateCards(companyId);
  const [editing, setEditing] = useState<RateCardView | null>(null);
  const money = (cents: number | null, currency: string) =>
    cents === null ? '—' : formatCents(cents, currency, intl(i18n.language));

  if (rates.isError) {
    return (
      <LoadError onRetry={() => void rates.refetch()}>{t('gov.ops.rates.loadError')}</LoadError>
    );
  }
  return (
    <>
      <DataTable
        label={t('gov.ops.rates.title')}
        rows={rates.data ?? []}
        getRowKey={(r) => r.localGovernmentId}
        isLoading={rates.isLoading}
        emptyLabel={t('gov.ops.rates.empty')}
        onRowClick={(r) => setEditing(r)}
        columns={[
          {
            key: 'town',
            label: t('gov.ops.rates.municipality'),
            primary: true,
            sortValue: (r) => r.localGovernmentName,
            cell: (r) => (
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.localGovernmentName}
                </Typography>
                {!r.configured && (
                  <Typography variant="caption" color="text.secondary">
                    {t('gov.ops.rates.notSet')}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            key: 'hourly',
            label: t('gov.ops.rates.hourly'),
            align: 'right',
            cell: (r) => money(r.hourlyRateCents, r.currency),
          },
          {
            key: 'fileFee',
            label: t('gov.ops.rates.fileFee'),
            align: 'right',
            cell: (r) => money(r.fileFeeCents, r.currency),
          },
          {
            key: 'unlock',
            label: t('gov.ops.rates.unlockFee'),
            align: 'right',
            cell: (r) => money(r.unlockFeeCents, r.currency),
          },
          {
            key: 'tax',
            label: t('gov.ops.rates.tax'),
            align: 'right',
            cell: (r) => `${new Intl.NumberFormat(intl(i18n.language)).format(r.taxRatePercent)} %`,
          },
          {
            key: 'terms',
            label: t('gov.ops.rates.terms'),
            align: 'right',
            cell: (r) => t('gov.ops.rates.termsDays', { count: r.paymentTermsDays }),
          },
          { ...actionsColumn, cell: (r) => <RowActions onEdit={() => setEditing(r)} /> },
        ]}
      />
      {editing && (
        <RateDialog companyId={companyId} card={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

/** A money box's value to cents: blank is allowed only where `optional`. */
function readMoney(text: string, locale: string, optional: boolean): number | null | 'invalid' {
  if (!text.trim()) return optional ? null : 'invalid';
  const cents = parseMoneyToCents(text, intl(locale));
  return cents === null ? 'invalid' : cents;
}

function RateDialog({
  companyId,
  card,
  onClose,
}: {
  companyId: string;
  card: RateCardView;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const save = useSaveRateCard(companyId);
  const lang = i18n.language;
  const [hourly, setHourly] = useState(
    card.configured ? centsForInput(card.hourlyRateCents, lang) : '',
  );
  const [fileFee, setFileFee] = useState(centsForInput(card.fileFeeCents, lang));
  const [unlock, setUnlock] = useState(centsForInput(card.unlockFeeCents, lang));
  const [tax, setTax] = useState(
    String(card.taxRatePercent).replace('.', lang.startsWith('fr') ? ',' : '.'),
  );
  const [terms, setTerms] = useState(String(card.paymentTermsDays));
  const [notes, setNotes] = useState(card.notes);

  const h = readMoney(hourly, lang, false);
  const f = readMoney(fileFee, lang, false);
  const u = readMoney(unlock, lang, true);
  const taxValue = Number(tax.replace(',', '.'));
  const taxOk =
    tax.trim() !== '' &&
    Number.isFinite(taxValue) &&
    taxValue >= 0 &&
    taxValue <= 100 &&
    /^\d+([.,]\d{1,2})?$/.test(tax.trim());
  const termsValue = Number(terms);
  const termsOk = Number.isInteger(termsValue) && termsValue >= 0 && termsValue <= 365;
  const valid = h !== 'invalid' && f !== 'invalid' && u !== 'invalid' && taxOk && termsOk;

  const submit = async () => {
    if (!valid) return;
    try {
      await save.mutateAsync({
        localGovernmentId: card.localGovernmentId,
        hourlyRateCents: h as number,
        fileFeeCents: f as number,
        unlockFeeCents: u as number | null,
        taxRatePercent: taxValue,
        paymentTermsDays: termsValue,
        notes,
      });
      toast.success('gov.ops.rates.saved');
      onClose();
    } catch (err) {
      toast.error(err, 'gov.ops.rates.saveFailed');
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.ops.rates.editTitle', { town: card.localGovernmentName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('gov.ops.rates.hourly')}
            value={hourly}
            onChange={(e) => setHourly(e.target.value)}
            error={h === 'invalid' && hourly !== ''}
            helperText={h === 'invalid' && hourly !== '' ? t('gov.ops.rates.badAmount') : ' '}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <TextField
            label={t('gov.ops.rates.fileFee')}
            value={fileFee}
            onChange={(e) => setFileFee(e.target.value)}
            error={f === 'invalid'}
            helperText={
              f === 'invalid' ? t('gov.ops.rates.badAmount') : t('gov.ops.rates.fileFeeHint')
            }
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <TextField
            label={t('gov.ops.rates.unlockFee')}
            value={unlock}
            onChange={(e) => setUnlock(e.target.value)}
            error={u === 'invalid'}
            helperText={
              u === 'invalid' ? t('gov.ops.rates.badAmount') : t('gov.ops.rates.unlockFeeHint')
            }
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('gov.ops.rates.taxPercent')}
              value={tax}
              onChange={(e) => setTax(e.target.value)}
              error={!taxOk}
              helperText={!taxOk ? t('gov.ops.rates.badTax') : t('gov.ops.rates.taxHint')}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              type="number"
              label={t('gov.ops.rates.termsLabel')}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              error={!termsOk}
              helperText={!termsOk ? t('gov.ops.rates.badTerms') : ' '}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { min: 0, max: 365 } }}
            />
          </Stack>
          <TextField
            label={t('gov.ops.rates.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('gov.ops.rates.appliesNote')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() => void submit()}
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── The town's side ─────────────────────────────────────────────────────────

function ReceivedInvoices({ lgId }: { lgId: string }) {
  const { t, i18n } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const invoices = useReceivedInvoices(lgId);
  const [open, setOpen] = useState<string | null>(null);
  const detail = useReceivedInvoice(lgId, open);

  if (invoices.isError) {
    return (
      <LoadError onRetry={() => void invoices.refetch()}>
        {t('gov.ops.invoices.loadError')}
      </LoadError>
    );
  }
  return (
    <>
      <DataTable
        label={t('gov.ops.invoices.receivedTitle')}
        rows={invoices.data ?? []}
        getRowKey={(i) => i.id}
        isLoading={invoices.isLoading}
        emptyLabel={t('gov.ops.invoices.receivedEmpty')}
        pageSize={25}
        initialSortKey="month"
        initialSortDir="desc"
        onRowClick={(i) => setOpen(i.id)}
        columns={[
          ...invoiceColumns(t, i18n.language, 'town'),
          { ...actionsColumn, cell: (i) => <RowActions onView={() => setOpen(i.id)} /> },
        ]}
      />
      <InvoiceDrawer
        open={!!open}
        onClose={() => setOpen(null)}
        invoice={detail.data}
        isLoading={detail.isLoading}
        isError={detail.isError}
        onRetry={() => void detail.refetch()}
        download={(format) =>
          detail.data && downloadInvoice({ localGovernmentId: lgId }, detail.data, format)
        }
      />
    </>
  );
}

// ── The invoice itself ─────────────────────────────────────────────────────

function InvoiceDrawer({
  open,
  onClose,
  invoice,
  isLoading,
  isError,
  onRetry,
  download,
  actions,
  children,
}: {
  open: boolean;
  onClose: () => void;
  invoice: ContractorInvoiceView | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  download: (format: 'docx' | 'pdf') => Promise<void> | undefined | null;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const [downloading, setDownloading] = useState<'docx' | 'pdf' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  useEffect(() => setDownloadError(null), [invoice?.id]);
  const money = (cents: number) =>
    formatCents(cents, invoice?.currency ?? 'CAD', intl(i18n.language));

  const save = async (format: 'docx' | 'pdf') => {
    setDownloading(format);
    setDownloadError(null);
    try {
      await download(format);
    } catch (err) {
      setDownloadError(apiErrorMessage(err, t('gov.ops.invoices.downloadFailed')));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{ width: { xs: '100vw', sm: 560 }, p: 3 }}
        role="region"
        aria-label={t('gov.ops.invoices.detailTitle')}
      >
        <Stack direction="row" sx={{ alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
            {t('gov.ops.invoices.detailTitle')}
          </Typography>
          <IconButton aria-label={t('common.actions.close')} onClick={onClose}>
            <Icons.X size={iconSize.md} aria-hidden />
          </IconButton>
        </Stack>
        {isLoading && <Loading />}
        {isError && <LoadError onRetry={onRetry}>{t('gov.ops.invoices.loadError')}</LoadError>}
        {invoice && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <InvoiceStatusChip invoice={invoice} />
              <Typography variant="body2" color="text.secondary">
                {invoice.number
                  ? t('gov.ops.invoices.numbered', { number: invoice.number })
                  : t('gov.ops.invoices.unnumbered')}
              </Typography>
            </Stack>
            <Box>
              <Typography variant="body2">
                <strong>{t('gov.ops.invoices.from')}</strong> {invoice.companyName}
              </Typography>
              <Typography variant="body2">
                <strong>{t('gov.ops.invoices.to')}</strong> {invoice.localGovernmentName}
              </Typography>
              <Typography variant="body2">
                <strong>{t('gov.ops.invoices.period')}</strong>{' '}
                {monthLabel(invoice.month, i18n.language)}
              </Typography>
              {invoice.issuedAt && (
                <Typography variant="body2">
                  <strong>{t('gov.ops.invoices.issued')}</strong>{' '}
                  {formatDate(invoice.issuedAt, i18n.language)}
                </Typography>
              )}
              {invoice.dueAt && (
                <Typography variant="body2">
                  <strong>{t('gov.ops.invoices.dueOn')}</strong>{' '}
                  {formatDate(invoice.dueAt, i18n.language)}
                </Typography>
              )}
              {invoice.paidAt && (
                <Typography variant="body2">
                  <strong>{t('gov.ops.invoices.paidOnLabel')}</strong>{' '}
                  {formatDate(invoice.paidAt, i18n.language)}
                  {invoice.paymentReference ? ` · ${invoice.paymentReference}` : ''}
                </Typography>
              )}
            </Box>
            <Divider />
            {(invoice.lines ?? []).length === 0 ? (
              <Alert severity="info">{t('gov.ops.invoices.noLines')}</Alert>
            ) : (
              <Table size="small" aria-label={t('gov.ops.invoices.linesTitle')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('gov.ops.invoices.description')}</TableCell>
                    <TableCell align="right">{t('gov.ops.invoices.quantity')}</TableCell>
                    <TableCell align="right">{t('gov.ops.invoices.amount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoice.lines ?? []).map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.description}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {l.kind === 'time' ? formatMinutes(l.quantity) : l.quantity}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {money(l.amountCents)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      {t('gov.ops.invoices.subtotal')}
                    </TableCell>
                    <TableCell align="right">{money(invoice.subtotalCents)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      {t('gov.ops.invoices.tax', {
                        rate: new Intl.NumberFormat(intl(i18n.language)).format(
                          invoice.taxRatePercent,
                        ),
                      })}
                    </TableCell>
                    <TableCell align="right">{money(invoice.taxCents)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>
                      {t('gov.ops.invoices.total')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {money(invoice.totalCents)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
            {invoice.notes && (
              <Typography variant="body2" color="text.secondary">
                {invoice.notes}
              </Typography>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Icons.FileWord size={iconSize.md} aria-hidden />}
                disabled={downloading !== null}
                onClick={() => void save('docx')}
              >
                {t('gov.ops.invoices.downloadWord')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Icons.FilePdf size={iconSize.md} aria-hidden />}
                disabled={downloading !== null}
                onClick={() => void save('pdf')}
              >
                {t('gov.ops.invoices.downloadPdf')}
              </Button>
            </Stack>
            {downloadError && <Alert severity="warning">{downloadError}</Alert>}
            {actions}
          </Stack>
        )}
        {children}
      </Box>
    </Drawer>
  );
}
